import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { EditGameDeveloperActionTypes } from '../editDeveloperModalActions';
import { editGameDeveloperReducer } from './editDeveloperModalReducer';
import {
	EditGameDeveloperContext,
	editGameDeveloperInitialState,
	TTypeOfEditGameDeveloperModalWindow,
} from './editDeveloperModalContext';
import { IUpdateGameDeveloper, IGetGameDeveloperInfo } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getGameDeveloperInfo } from 'backend/games';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	GameDevelopersContext,
	IGameDevelopersContext,
} from 'modules/Entertainment/Games/GameDevelopers/gameDevelopersState/gameDevelopersContext';

const EditGameDeveloperState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editGameDeveloperReducer, editGameDeveloperInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { IDForEdit } = useContext<IGameDevelopersContext>(GameDevelopersContext);

	const { response, loading, refetch } = useFetchData<IGetGameDeveloperInfo, string>(
		IDForEdit ? getGameDeveloperInfo : null,
		IDForEdit || '',
	);

	useEffect(() => {
		refetch();
	}, [IDForEdit]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditGameDeveloperActionTypes.SET_ALL_DATA,
				payload: {
					id: response.data.gameDeveloper.id,
					name: response.data.gameDeveloper.name || '',
				},
			});
			dispatch({
				type: EditGameDeveloperActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.gameDeveloper.name || '',
					},
				},
			});
		}
	}, [response]);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditGameDeveloperActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IUpdateGameDeveloper | null) => {
		dispatch({
			type: EditGameDeveloperActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditGameDeveloperModalWindow) => {
		dispatch({
			type: EditGameDeveloperActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditGameDeveloperActionTypes.RESET_FIELDS }), []);

	return (
		<EditGameDeveloperContext.Provider
			value={{
				changeName,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				initialFormData: state.initialFormData,
				developerID: state.developerID,
				name: state.name,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditGameDeveloperContext.Provider>
	);
};

export default React.memo(EditGameDeveloperState);
